"use client";

import { useEffect, useRef, useState } from "react";
import { buttonClass } from "@/components/ui/button";
import { rememberSlateContext } from "@/lib/analytics/feed-slate";
import { useAnalytics } from "@/lib/analytics/hooks";
import { setClientCookie } from "@/lib/cookies/client";
import {
	HUNT_ENTRY_COOKIE,
	HUNT_ENTRY_MAX_AGE_S,
	huntEntryCookieDomain,
} from "@/lib/hunt/hunt-rank-log";

export const FEED_HUNT_CARD_IMPRESSION_EVENT = "feed_hunt_card_impression";
export const FEED_HUNT_CARD_CLICKED_EVENT = "feed_hunt_card_clicked";
/** `?src=` the seeker page admits a guest on (anon Hunt), and the entry surface. */
export const FEED_HUNT_CARD_SRC = "feed_hunt_card";

const COPY = {
	en: {
		eyebrow: "Hunt · AI job match",
		title: "Tired of scrolling? Let Hunt pick for you",
		body: "Tell Hunt your skills and it shows the best-fit jobs one at a time.",
		cta: "Try Hunt",
	},
	id: {
		eyebrow: "Hunt · AI pencari kerja",
		title: "Capek scroll? Biar Hunt yang pilihkan",
		body: "Ceritakan skill kamu, Hunt tunjukkan lowongan paling cocok satu per satu.",
		cta: "Coba Hunt",
	},
} as const;

export function feedHuntCardHref(locale: string): string {
	return `/${locale === "en" ? "en" : "id"}/seeker?src=${FEED_HUNT_CARD_SRC}`;
}

/** sessionStorage stash key for the Hunt card's slate (not a job id). */
const FEED_HUNT_CARD_SLATE_KEY = "feed-hunt-card";

/**
 * Hunt entry card in the /jobs feed (`huntCard` knob of `feed-rank-policy`,
 * src/lib/personalization/hunt-card-slot.ts). Links to the Hunt chat at
 * /seeker: guests land in the anon Hunt (the seeker page admits
 * `src=feed_hunt_card`), signed-in visitors in their own Hunt. Same entry
 * the hero and HuntSpark use, with its own `src` so the session is
 * attributable (`entry_surface` on the Hunt card events).
 */
export function FeedHuntCard({
	locale,
	slot,
	rankPolicy,
	slateId,
}: {
	locale: string;
	slot: number;
	rankPolicy: string | null;
	slateId: string | undefined;
}) {
	const { posthog } = useAnalytics();
	const ref = useRef<HTMLElement>(null);
	const firedFor = useRef<string | null>(null);
	// Mirrors the impression onto the DOM: PostHog never inits on localhost,
	// so e2e reads this instead of the event (same idea as data-slate-length).
	const [impressedSlate, setImpressedSlate] = useState<string | null>(null);
	const copy = locale === "en" ? COPY.en : COPY.id;

	useEffect(() => {
		// One impression per slate, once at least half the card is on screen.
		if (!slateId || firedFor.current === slateId) return;
		const fire = () => {
			if (firedFor.current === slateId) return;
			firedFor.current = slateId;
			posthog?.capture(FEED_HUNT_CARD_IMPRESSION_EVENT, {
				rank_policy: rankPolicy,
				slate_id: slateId,
				slot,
				locale,
			});
			setImpressedSlate(slateId);
		};
		const el = ref.current;
		if (!el || typeof IntersectionObserver === "undefined") {
			fire();
			return;
		}
		const io = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					fire();
					io.disconnect();
				}
			},
			{ threshold: 0.5 },
		);
		io.observe(el);
		return () => io.disconnect();
	}, [slateId, rankPolicy, slot, locale, posthog]);

	return (
		<article
			ref={ref}
			data-testid="feed-hunt-card"
			data-slot={slot}
			data-impression-slate={impressedSlate ?? undefined}
			className="border-b border-border px-1 py-4 sm:px-2"
		>
			<a
				href={feedHuntCardHref(locale)}
				data-testid="feed-hunt-card-cta"
				onClick={() => {
					posthog?.capture(FEED_HUNT_CARD_CLICKED_EVENT, {
						rank_policy: rankPolicy,
						slate_id: slateId ?? null,
						slot,
						locale,
					});
					// A Hunt checkout started after this click is credited to the
					// slate (last-touch, read by `recallLatestSlateContext`).
					if (slateId) {
						rememberSlateContext(FEED_HUNT_CARD_SLATE_KEY, {
							slate_id: slateId,
							position: slot,
							rank_policy: rankPolicy,
						});
					}
					try {
						setClientCookie(HUNT_ENTRY_COOKIE, FEED_HUNT_CARD_SRC, {
							maxAgeSeconds: HUNT_ENTRY_MAX_AGE_S,
							domain: huntEntryCookieDomain(window.location.hostname),
						});
					} catch {
						// Cookies blocked: the link still works, entry_surface is null.
					}
				}}
				className="group flex flex-col gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4 transition-colors hover:border-primary/60 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:flex-row sm:items-center sm:justify-between"
			>
				<span className="flex min-w-0 items-start gap-3">
					<span className="flex min-w-0 flex-col gap-1">
						<span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
							{copy.eyebrow}
						</span>
						<span className="text-base font-semibold text-foreground">
							{copy.title}
						</span>
						<span className="text-sm text-muted-foreground">{copy.body}</span>
					</span>
				</span>
				<span
					className={buttonClass({
						size: "sm",
						className: "min-h-11 shrink-0 group-hover:bg-primary/90",
					})}
				>
					{copy.cta}
				</span>
			</a>
		</article>
	);
}
